import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Instagram, Facebook } from 'lucide-react';
import { sanityClient } from '../lib/sanityClient';

export default function Footer({ onShowAdmin }) {
  const [contact, setContact] = useState({
    email: 'info@studiosoraa.no',
    phone: '+47 XXX XX XXX',
    address: 'Studio Søraa'
  });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const result = await sanityClient.fetch(
          `*[_type == "footerContact"][0] {
            email,
            phone,
            address
          }`
        );
        console.log('Fetched footer contact:', result);
        if (result) {
          setContact(result);
        } else {
          console.log('No footer contact document found');
        }
      } catch (error) {
        console.error('Error fetching footer contact:', error);
      }
    };

    fetchContact();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="w-full relative overflow-hidden py-12 md:py-20 bg-Pink-BG">
      <motion.div
        className="pt-8 md:pt-20 pb-8 md:pb-12 flex flex-col justify-center items-center gap-12 md:gap-24 w-full px-4 md:px-10 lg:px-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div
          className="hidden"
          variants={itemVariants}
        >
          <motion.div className="justify-start text-Neautral-main text-lg sm:text-xl md:text-2xl lg:text-3xl font-normal font-opensans tracking-tight max-w-xs md:max-w-sm">
            Meld deg på for gode tilbud
          </motion.div>
          <motion.div className="w-full md:w-96 pl-3 md:pl-5 pr-2 md:pr-4 py-3 md:py-4 rounded-[20px] bg-Neautral-white border border-Neautral-base flex justify-between items-center">
            <input
              type="email"
              placeholder="E-post adresse"
              className="bg-transparent outline-none text-Neautral-main text-xs sm:text-sm md:text-base font-semibold font-opensans leading-5 tracking-tight flex-1 placeholder-Neautral-light"
            />
            <motion.button
              className="px-2 sm:px-3 md:px-5 py-2 rounded-[50px] flex justify-center items-center gap-2 cursor-pointer hover:opacity-80 transition flex-shrink-0"
              style={{ backgroundColor: '#6b705c' }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="text-center justify-start text-white text-[10px] sm:text-xs md:text-base font-semibold font-opensans tracking-tight">
                send
              </div>
              <Send size={16} className="text-white" strokeWidth={2.5} />
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Footer Links */}
        <motion.div className="w-full flex flex-col justify-start items-start gap-8 md:gap-10" variants={containerVariants}>
          <motion.div className="self-stretch flex flex-col md:flex-row justify-between items-start md:items-start gap-8 md:gap-16" variants={containerVariants}>
            {/* Navigasjon Seksjon */}
            <motion.div className="flex flex-col justify-start items-start gap-4" variants={itemVariants}>
              <motion.div className="text-Neautral-main text-sm sm:text-sm md:text-base font-semibold font-opensans tracking-tight">
                Navigasjon
              </motion.div>
              <motion.div className="flex flex-col justify-start items-start gap-3">
                <motion.div
                  className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition"
                  whileHover={{ x: 4 }}
                  onClick={() => scrollToSection('booking')}
                >
                  Bestill time
                </motion.div>
                <motion.div
                  className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition"
                  whileHover={{ x: 4 }}
                  onClick={() => scrollToSection('trending-deals')}
                >
                  Galleri
                </motion.div>
                <motion.div
                  className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition"
                  whileHover={{ x: 4 }}
                  onClick={() => scrollToSection('glowgenics')}
                >
                  Om oss
                </motion.div>
                <motion.div
                  className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition"
                  whileHover={{ x: 4 }}
                  onClick={() => scrollToSection('contact')}
                >
                  Kontakt
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Kontakt Seksjon */}
            <motion.div className="flex flex-col justify-start items-start gap-4" variants={itemVariants}>
              <motion.div className="text-Neautral-main text-sm sm:text-sm md:text-base font-semibold font-opensans tracking-tight">
                Kontakt
              </motion.div>
              <motion.div className="flex flex-col justify-start items-start gap-3">
                <motion.div className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition" whileHover={{ x: 4 }}>
                  E-post: {contact.email}
                </motion.div>
                <motion.div className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition" whileHover={{ x: 4 }}>
                  Telefon: {contact.phone}
                </motion.div>
                <motion.div className="text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans cursor-pointer hover:text-Pink-main transition" whileHover={{ x: 4 }}>
                  Adresse: {contact.address}
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Sosiale Medier Seksjon */}
            <motion.div className="flex flex-col justify-start items-start gap-4" variants={itemVariants}>
              <motion.div className="text-Neautral-main text-sm sm:text-sm md:text-base font-semibold font-opensans tracking-tight">
                Følg Oss
              </motion.div>
              <motion.div className="flex justify-start items-center gap-4">
                <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.95 }}>
                  <Instagram size={28} className="text-Neautral-main cursor-pointer hover:text-Pink-main transition" strokeWidth={1.5} />
                </motion.div>
                <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.95 }}>
                  <Facebook size={28} className="text-Neautral-main cursor-pointer hover:text-Pink-main transition" strokeWidth={1.5} />
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Bottom Section - Centered */}
          <motion.div className="w-full flex flex-col justify-center items-center gap-4" variants={itemVariants}>
            <motion.div className="text-center text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans leading-5 tracking-tight">
              © 2025 Studio Søraa. Alle rettigheter reservert.
            </motion.div>
            <motion.div className="text-center text-Neautral-main text-xs sm:text-xs md:text-sm font-normal font-opensans leading-5 tracking-tight flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
              <span>
                Laget av{' '}
                <a href="https://skapweb.no" target="_blank" rel="noopener noreferrer" className="text-Neautral-main hover:text-Pink-main transition">
                  SkapWeb
                </a>
              </span>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
